import { createTheme } from "@mui/material/styles";

// Central brand theme so every MUI component (Button, TextField, Dialog,
// CircularProgress, ...) picks up consistent colors/spacing/typography
// instead of each page hardcoding #104581 and one-off styles.
const theme = createTheme({
  palette: {
    primary: {
      main: "#104581",
    },
    secondary: {
      main: "#16C2CF",
    },
    error: {
      main: "#b91c1c",
    },
    background: {
      default: "#F8F9FF",
    },
  },
  typography: {
    fontFamily: '"Jost", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 10,
        },
        outlined: {
          borderWidth: 2,
          "&:hover": {
            borderWidth: 2,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        fullWidth: true,
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    // Consistent "card" table look shared by every table in the app — every
    // page just renders TableContainer/TableCell and gets this for free
    // instead of each page hand-rolling its own table styling.
    MuiTableContainer: {
      styleOverrides: {
        root: {
          border: "1px solid #e2e8f0",
          borderRadius: 0,
          boxSizing: "border-box",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
          "&.MuiPaper-root, &.MuiPaper-rounded": {
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
            borderRadius: 0,
            border: "1px solid #e2e8f0",
            boxSizing: "border-box",
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "#f8fafc",
          "& .MuiTableCell-root": {
            borderBottom: "2px solid #e2e8f0",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        // `height` on a table cell is a floor, not a cap — content taller
        // than this (wrapped text, etc.) still grows the row. Without it,
        // row height was purely whatever each page's cell content happened
        // to need: plain-text tables ended up ~40px/row while tables with a
        // default-size IconButton in the actions column ended up ~64px/row,
        // even though every table shared this same padding. This floor is
        // tall enough to hold a default (medium) IconButton, so every table
        // in the app lines up at the same minimum row height.
        root: {
          height: "56px",
          borderBottom: "1px solid #f1f5f9",
          padding: "12px 16px",
          color: "#475569",
          fontSize: 14,
          boxSizing: "border-box",
        },
        head: {
          height: "48px",
          color: "#1e293b",
          fontSize: 12,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          padding: "14px 16px",
          boxSizing: "border-box",
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-root:hover": {
            backgroundColor: "#f8fafc",
          },
          "& .MuiTableRow-root:last-child .MuiTableCell-root": {
            borderBottom: "none",
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#104581",
          color: "#ffffff",
          fontSize: "12px",
          fontWeight: 500,
          borderRadius: "6px",
          padding: "6px 10px",
          fontFamily: '"Jost", sans-serif',
          boxShadow: "0px 4px 12px rgba(15, 23, 42, 0.15)",
        },
        arrow: {
          color: "#104581",
        },
      },
    },
  },
});

export default theme;
